import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Lightweight picker used by "Merge into another plant…" on the plant
 * detail view. Shows all OTHER plants filtered by common/scientific
 * name; auto-suggests likely duplicates at the top (same normalised
 * common name).
 */
export const MergeTargetPicker = ({ sourcePlant, allPlants, onPick, onClose }) => {
  const { t } = useLanguage();
  const [query, setQuery] = useState('');

  const { suggestions, remaining } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sourceName = (sourcePlant?.common_name || '').trim().toLowerCase();
    const others = (allPlants || []).filter(p => p.plant_id !== sourcePlant?.plant_id && !p.archived);
    const matches = q
      ? others.filter(p =>
          (p.common_name || '').toLowerCase().includes(q) ||
          (p.scientific_name || '').toLowerCase().includes(q))
      : others;
    // Same-common-name candidates float to the top as "likely duplicates".
    const suggestions = sourceName
      ? matches.filter(p => (p.common_name || '').trim().toLowerCase() === sourceName)
      : [];
    const suggestionIds = new Set(suggestions.map(p => p.plant_id));
    const remaining = matches.filter(p => !suggestionIds.has(p.plant_id));
    return { suggestions, remaining };
  }, [allPlants, sourcePlant, query]);

  return (
    <Dialog open={!!sourcePlant} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DialogContent
        className="max-w-lg max-h-[85vh] overflow-y-auto"
        aria-describedby={undefined}
        data-testid="merge-picker-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display" style={{ color: 'hsl(143, 28%, 25%)' }}>
            {t('mergePickerTitle') || 'Merge into another plant'}
          </DialogTitle>
          <DialogDescription>
            {(t('mergePickerBlurb') || 'Choose the plant that should stay (the Keeper). {name} will be absorbed into it.').replace('{name}', sourcePlant?.common_name || '')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'hsl(125, 12%, 45%)' }} />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('mergePickerSearch') || 'Search by name…'}
              className="pl-9"
              data-testid="merge-picker-search"
            />
          </div>

          {suggestions.length > 0 && (
            <div className="space-y-2" data-testid="merge-picker-suggestions">
              <div className="text-[11px] font-semibold uppercase" style={{ color: 'hsl(143, 28%, 45%)' }}>
                {t('mergePickerSuggested') || 'Likely duplicates'}
              </div>
              {suggestions.map(p => (
                <PickerRow key={p.plant_id} plant={p} onPick={onPick} highlight />
              ))}
            </div>
          )}

          <div className="space-y-2" data-testid="merge-picker-list">
            {remaining.length === 0 && suggestions.length === 0 ? (
              <p className="text-sm text-center py-8" style={{ color: 'hsl(125, 12%, 45%)' }}>
                {t('mergePickerEmpty') || 'No other plants match.'}
              </p>
            ) : (
              remaining.map(p => (
                <PickerRow key={p.plant_id} plant={p} onPick={onPick} />
              ))
            )}
          </div>
        </div>

        <div className="flex justify-end pt-3">
          <Button onClick={onClose} variant="outline" data-testid="merge-picker-cancel">
            {t('cancel') || 'Cancel'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PickerRow = ({ plant, onPick, highlight = false }) => (
  <button
    type="button"
    onClick={() => onPick?.(plant)}
    className="w-full text-left rounded-lg p-3 border transition-all hover:shadow-sm"
    style={{
      borderColor: highlight ? 'hsl(143, 28%, 65%)' : 'hsl(143, 28%, 88%)',
      backgroundColor: highlight ? 'hsl(143, 28%, 97%)' : 'white',
    }}
    data-testid={`merge-picker-row-${plant.plant_id}`}
  >
    <div className="font-medium text-sm" style={{ color: 'hsl(143, 28%, 25%)' }}>{plant.common_name}</div>
    {plant.scientific_name && (
      <div className="text-xs italic" style={{ color: 'hsl(125, 12%, 40%)' }}>{plant.scientific_name}</div>
    )}
  </button>
);
